import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { 
  MapPin, 
  Users, 
  Utensils, 
  Apple,
  Dumbbell,
  Heart,
  Sparkles,
  Activity,
  Target,
  Clock,
  TrendingUp,
  Search,
  Star,
  CheckCircle,
  ChevronRight,
  BarChart3,
  Camera,
  PieChart,
  Calendar,
  Zap,
  Building,
  Leaf,
  UserCheck,
  ArrowRight,
  Cpu,
  Shield,
  Smartphone,
  Check
} from "lucide-react";
import Navigation from "../components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// ============================================
// 1. HERO SECTION
// ============================================
const HeroSection = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/waitlist`, { email });
      if (response.data.success) {
        toast.success(response.data.message);
        setEmail("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=85&w=1920&auto=format&fit=crop"
          alt="Fitness lifestyle"
          className="w-full h-full object-cover"
        />
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-black/70" />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-24 md:py-32">
        {/* Centered Top Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="flex justify-center mb-12"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_evolve-fitness-5/artifacts/uqt72vx4_IMG_1561.png" 
            alt="EVOLVE" 
            className="h-48 md:h-56 lg:h-72 w-auto object-contain"
            data-testid="hero-logo"
          />
        </motion.div>

        <div className="max-w-3xl mx-auto text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FFD1]/10 border border-[#00FFD1]/30 mb-8"
          >
            <Sparkles className="w-4 h-4 text-[#00FFD1]" />
            <span className="text-[#00FFD1] text-sm font-medium">AI-Powered Fitness Network</span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6"
          >
            Your Fitness Network.{" "}
            <span className="text-[#00FFD1]">Powered by AI.</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl text-gray-300 leading-relaxed mb-10 max-w-2xl mx-auto"
          >
            Discover personal trainers, meal prep services, and wellness professionals in your area while tracking calories, food, workouts, and progress in one intelligent platform.
          </motion.p>

          {/* Email Capture Form */}
          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-3 mb-6 justify-center"
          >
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 bg-white/10 border border-white/20 text-white placeholder:text-gray-400 rounded-full h-14 px-6 focus:border-[#00FFD1] focus:ring-1 focus:ring-[#00FFD1] transition-all outline-none"
              data-testid="hero-email-input"
              required
            />
            <button
              type="submit"
              disabled={isLoading}
              className="h-14 px-8 bg-[#00FFD1] text-black font-bold rounded-full hover:bg-[#00FFD1]/90 transition-all shadow-[0_0_20px_rgba(0,255,209,0.3)] hover:shadow-[0_0_30px_rgba(0,255,209,0.5)] disabled:opacity-70 flex items-center justify-center gap-2"
              data-testid="hero-submit-btn"
            >
              {isLoading ? "Joining..." : "Join Early Access"}
              {!isLoading && <ChevronRight className="w-4 h-4" />}
            </button>
          </motion.form>

          {/* Secondary CTA */}
          <motion.a
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            href="/partner-apply"
            className="inline-flex items-center gap-2 text-white/80 hover:text-[#00FFD1] transition-colors group justify-center"
            data-testid="hero-partner-cta"
          >
            <span className="font-medium">Apply as a Trainer or Wellness Partner</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </motion.a>

          {/* Social Proof */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-12 text-gray-400 text-sm flex items-center gap-2 justify-center"
          >
            <span className="w-2 h-2 rounded-full bg-[#00FFD1] animate-pulse" />
            Join the next evolution of local fitness and wellness.
          </motion.p>
        </div>
      </div>
    </section>
  );
};

// ============================================
// 2. FEATURES SECTION - PREMIUM REBUILD
// ============================================
const FeaturesSection = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/waitlist`, { email });
      if (response.data.success) {
        toast.success(response.data.message);
        setEmail("");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Smooth scroll helper
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  const features = [
    {
      id: "trainers",
      scrollTo: "trainers",
      badge: "Local Discovery",
      badgeIcon: <MapPin className="w-3.5 h-3.5" />,
      title: "Train Smarter, Not Harder",
      description: "Discover top-rated trainers in your area, view real results, and book sessions instantly.",
      visual: "trainers"
    },
    {
      id: "nutrition",
      scrollTo: "technology",
      badge: "AI-Powered",
      badgeIcon: <Sparkles className="w-3.5 h-3.5" />,
      title: "Track Food in Seconds",
      description: "Snap a photo or scan a barcode. EVOLVE instantly logs your meals and tracks your macros.",
      visual: "nutrition"
    },
    {
      id: "mealprep",
      scrollTo: "features",
      badge: "Local Services",
      badgeIcon: <Utensils className="w-3.5 h-3.5" />,
      title: "Find Better Fuel",
      description: "Locate healthy restaurants and meal prep services near you — all inside the app.",
      visual: "mealprep"
    },
    {
      id: "dashboard",
      scrollTo: "technology",
      badge: "All-in-One",
      badgeIcon: <Activity className="w-3.5 h-3.5" />,
      title: "Everything Connected",
      description: "Your workouts, nutrition, recovery, and progress — all tracked in one place.",
      visual: "dashboard"
    }
  ];

  // Trainer Visual Component - Realistic iPhone with Map
  const TrainerVisual = () => (
    <div className="relative w-full max-w-[320px] mx-auto">
      {/* iPhone Frame */}
      <div className="relative bg-gradient-to-b from-[#2a2a2e] to-[#1a1a1e] rounded-[3rem] p-3 shadow-2xl border border-white/10">
        {/* Dynamic Island */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-7 bg-black rounded-full z-30" />
        
        <div className="relative bg-black rounded-[2.5rem] overflow-hidden">
          {/* Screen */}
          <div className="relative aspect-[9/19.5]">
            {/* Real Map Background */}
            <div className="absolute inset-0">
              <img 
                src="https://images.unsplash.com/photo-1544411047-c491e34a24e0?w=400&h=800&fit=crop" 
                alt="City map"
                className="w-full h-full object-cover opacity-80"
              />
              {/* Dark overlay for better contrast */}
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/70" />
            </div>
              
            {/* Trainer Location Pins */}
            <motion.div 
              className="absolute top-[25%] left-[20%] w-10 h-10 bg-[#00FFD1] rounded-full flex items-center justify-center shadow-[0_0_25px_rgba(0,255,209,0.6)] cursor-pointer"
              animate={{ y: [0, -6, 0] }}
              transition={{ repeat: Infinity, duration: 2 }}
              whileHover={{ scale: 1.15 }}
            >
              <Users className="w-5 h-5 text-black" />
            </motion.div>
            <motion.div 
              className="absolute top-[40%] right-[15%] w-10 h-10 bg-[#00FFD1] rounded-full flex items-center justify-center shadow-[0_0_25px_rgba(0,255,209,0.6)] cursor-pointer"
              animate={{ y: [0, -6, 0] }}
              transition={{ repeat: Infinity, duration: 2, delay: 0.5 }}
              whileHover={{ scale: 1.15 }}
            >
              <Users className="w-5 h-5 text-black" />
            </motion.div>
            <motion.div 
              className="absolute top-[58%] left-[35%] w-10 h-10 bg-[#00FFD1] rounded-full flex items-center justify-center shadow-[0_0_25px_rgba(0,255,209,0.6)] cursor-pointer"
              animate={{ y: [0, -6, 0] }}
              transition={{ repeat: Infinity, duration: 2, delay: 1 }}
              whileHover={{ scale: 1.15 }}
            >
              <Users className="w-5 h-5 text-black" />
            </motion.div>

            {/* Trainer Card Overlay */}
            <motion.div 
              className="absolute bottom-6 left-3 right-3"
              whileHover={{ y: -2 }}
            >
              <div className="bg-black/90 backdrop-blur-xl rounded-2xl p-3 border border-white/20 cursor-pointer hover:border-[#00FFD1]/40 transition-colors shadow-2xl">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00FFD1]/40 to-[#00FFD1]/20 flex items-center justify-center border border-[#00FFD1]/30">
                    <Users className="w-5 h-5 text-[#00FFD1]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-sm font-semibold">Sarah Mitchell</p>
                    <p className="text-[#00FFD1] text-xs">NASM Certified • 0.8 mi</p>
                  </div>
                  <div className="flex items-center gap-1 bg-[#00FFD1]/20 px-2 py-1 rounded-full border border-[#00FFD1]/30">
                    <Star className="w-3 h-3 text-[#00FFD1] fill-[#00FFD1]" />
                    <span className="text-white text-xs font-bold">4.9</span>
                  </div>
                </div>
                <motion.button 
                  className="w-full py-2.5 bg-[#00FFD1] text-black text-xs font-bold rounded-xl"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  View Profile
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      {/* Glow */}
      <div className="absolute -inset-10 bg-[#00FFD1]/10 rounded-full blur-3xl -z-10" />
    </div>
  );

  // Nutrition Visual Component - Realistic iPhone with Chicken Breast
  const NutritionVisual = () => (
    <div className="relative w-full max-w-[320px] mx-auto">
      {/* iPhone Frame */}
      <div className="relative bg-gradient-to-b from-[#2a2a2e] to-[#1a1a1e] rounded-[3rem] p-3 shadow-2xl border border-white/10">
        {/* Dynamic Island */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-7 bg-black rounded-full z-30" />
        
        <div className="relative bg-black rounded-[2.5rem] overflow-hidden">
          <div className="relative aspect-[9/19.5] bg-[#0A0A0A]">
            {/* Camera viewfinder effect */}
            <div className="absolute inset-6 mt-8 border-2 border-[#00FFD1]/60 rounded-2xl z-10">
              <div className="absolute -top-1 -left-1 w-5 h-5 border-t-2 border-l-2 border-[#00FFD1]" />
              <div className="absolute -top-1 -right-1 w-5 h-5 border-t-2 border-r-2 border-[#00FFD1]" />
              <div className="absolute -bottom-1 -left-1 w-5 h-5 border-b-2 border-l-2 border-[#00FFD1]" />
              <div className="absolute -bottom-1 -right-1 w-5 h-5 border-b-2 border-r-2 border-[#00FFD1]" />
            </div>

            {/* Scanning animation */}
            <motion.div
              className="absolute left-6 right-6 h-0.5 bg-gradient-to-r from-transparent via-[#00FFD1] to-transparent z-20"
              animate={{ top: ["25%", "65%", "25%"] }}
              transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
            />

            {/* Real Chicken Breast Image */}
            <div className="absolute inset-0 pt-10 pb-28 px-8 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_evolve-fitness-5/artifacts/w77mxnba_IMG_2353.jpeg" 
                alt="Grilled chicken breast"
                className="w-full h-full object-cover rounded-xl shadow-2xl"
              />
            </div>

            {/* Recognition result - Chicken Breast Macros */}
            <motion.div 
              className="absolute bottom-5 left-3 right-3 z-20"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <div className="bg-[#00FFD1] rounded-2xl p-4 shadow-2xl">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-black/60 text-[10px] font-medium uppercase tracking-wide">AI Detected</p>
                    <p className="text-black text-base font-bold">Chicken Breast</p>
                  </div>
                  <div className="text-right">
                    <p className="text-black/60 text-[10px]">Calories</p>
                    <p className="text-black text-xl font-black">165</p>
                  </div>
                </div>
                <div className="flex justify-between pt-2 border-t border-black/10">
                  <div className="text-center">
                    <p className="text-black text-sm font-bold">31g</p>
                    <p className="text-black/60 text-[10px]">Protein</p>
                  </div>
                  <div className="text-center">
                    <p className="text-black text-sm font-bold">3.6g</p>
                    <p className="text-black/60 text-[10px]">Fat</p>
                  </div>
                  <div className="text-center">
                    <p className="text-black text-sm font-bold">0g</p>
                    <p className="text-black/60 text-[10px]">Carbs</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      <div className="absolute -inset-10 bg-[#00FFD1]/10 rounded-full blur-3xl -z-10" />
    </div>
  );

  // Meal Prep Visual Component - Realistic iPhone
  const MealPrepVisual = () => (
    <div className="relative w-full max-w-[320px] mx-auto">
      {/* iPhone Frame */}
      <div className="relative bg-gradient-to-b from-[#2a2a2e] to-[#1a1a1e] rounded-[3rem] p-3 shadow-2xl border border-white/10">
        {/* Dynamic Island */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-7 bg-black rounded-full z-30" />
        
        <div className="relative bg-black rounded-[2.5rem] overflow-hidden">
          <div className="relative aspect-[9/19.5] bg-[#0A0A0A]">
            {/* Header */}
            <div className="p-4 pt-12 border-b border-white/5">
              <p className="text-gray-400 text-xs">Near you</p>
              <p className="text-white font-bold text-lg">Healthy Options</p>
            </div>

            {/* Meal Prep Cards */}
            <div className="p-3 space-y-2">
              {[
                { name: "Clean Eats Kitchen", type: "Meal Prep", dist: "0.5 mi", rating: "4.9" },
                { name: "Macro Friendly", type: "Restaurant", dist: "1.2 mi", rating: "4.7" },
                { name: "Fit Fuel Co.", type: "Meal Prep", dist: "1.8 mi", rating: "4.8" },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: i * 0.2 }}
                  whileHover={{ scale: 1.02, x: 4 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-white/5 cursor-pointer hover:border-[#00FFD1]/30 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500/30 to-teal-500/30 flex items-center justify-center">
                    <Utensils className="w-5 h-5 text-[#00FFD1]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-sm font-medium">{item.name}</p>
                    <p className="text-gray-500 text-xs">{item.type} • {item.dist}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Star className="w-3 h-3 text-[#00FFD1] fill-[#00FFD1]" />
                    <span className="text-white text-xs">{item.rating}</span>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Bottom action */}
            <div className="absolute bottom-6 left-3 right-3">
              <motion.div 
                className="bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-xl p-3 text-center cursor-pointer"
                whileHover={{ scale: 1.02, backgroundColor: "rgba(0,255,209,0.15)" }}
                whileTap={{ scale: 0.98 }}
              >
                <p className="text-[#00FFD1] text-sm font-semibold">View 24 more nearby</p>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute -inset-10 bg-[#00FFD1]/10 rounded-full blur-3xl -z-10" />
    </div>
  );

  // Dashboard Visual Component - Realistic iPhone
  const DashboardVisual = () => (
    <div className="relative w-full max-w-[320px] mx-auto">
      {/* iPhone Frame */}
      <div className="relative bg-gradient-to-b from-[#2a2a2e] to-[#1a1a1e] rounded-[3rem] p-3 shadow-2xl border border-white/10">
        {/* Dynamic Island */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-7 bg-black rounded-full z-30" />
        
        <div className="relative bg-black rounded-[2.5rem] overflow-hidden">
          <div className="relative aspect-[9/19.5] bg-[#0A0A0A] p-4 pt-12">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <motion.div 
                className="bg-gradient-to-br from-[#00FFD1]/20 to-[#00FFD1]/5 rounded-xl p-3 border border-[#00FFD1]/20"
                whileHover={{ scale: 1.02 }}
              >
                <Activity className="w-5 h-5 text-[#00FFD1] mb-2" />
                <p className="text-white text-xl font-black">1,847</p>
                <p className="text-gray-400 text-[10px]">Calories Today</p>
              </motion.div>
              <motion.div 
                className="bg-white/5 rounded-xl p-3 border border-white/5"
                whileHover={{ scale: 1.02 }}
              >
                <Dumbbell className="w-5 h-5 text-blue-400 mb-2" />
                <p className="text-white text-xl font-black">4</p>
                <p className="text-gray-400 text-[10px]">Workouts This Week</p>
              </motion.div>
              <motion.div 
                className="bg-white/5 rounded-xl p-3 border border-white/5"
                whileHover={{ scale: 1.02 }}
              >
                <TrendingUp className="w-5 h-5 text-green-400 mb-2" />
                <p className="text-white text-xl font-black">-3.2</p>
                <p className="text-gray-400 text-[10px]">lbs This Month</p>
              </motion.div>
              <motion.div 
                className="bg-white/5 rounded-xl p-3 border border-white/5"
                whileHover={{ scale: 1.02 }}
              >
                <Heart className="w-5 h-5 text-red-400 mb-2" />
                <p className="text-white text-xl font-black">92%</p>
                <p className="text-gray-400 text-[10px]">Recovery Score</p>
              </motion.div>
            </div>

            {/* Progress Bar */}
            <div className="bg-white/5 rounded-xl p-3 border border-white/5 mb-3">
              <div className="flex justify-between items-center mb-2">
                <p className="text-white text-xs font-medium">Weekly Goal</p>
                <p className="text-[#00FFD1] text-xs font-bold">72%</p>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <motion.div 
                  className="h-full bg-gradient-to-r from-[#00FFD1] to-[#00D4AA] rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: "72%" }}
                  transition={{ duration: 1, delay: 0.5 }}
                />
              </div>
            </div>

            {/* Recent Activity */}
            <div className="space-y-2">
              <p className="text-gray-400 text-[10px] uppercase tracking-wider">Today</p>
              {[
                { icon: <Dumbbell className="w-3 h-3" />, text: "Upper Body Workout", time: "8:30 AM" },
                { icon: <Utensils className="w-3 h-3" />, text: "Logged Breakfast", time: "9:15 AM" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-white/[0.02] rounded-lg">
                  <div className="w-6 h-6 rounded-full bg-[#00FFD1]/10 flex items-center justify-center text-[#00FFD1]">
                    {item.icon}
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-xs">{item.text}</p>
                  </div>
                  <p className="text-gray-500 text-[10px]">{item.time}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="absolute -inset-10 bg-[#00FFD1]/10 rounded-full blur-3xl -z-10" />
    </div>
  );

  const getVisual = (type) => {
    switch(type) {
      case "trainers": return <TrainerVisual />;
      case "nutrition": return <NutritionVisual />;
      case "mealprep": return <MealPrepVisual />;
      case "dashboard": return <DashboardVisual />;
      default: return null;
    }
  };

  return (
    <section id="features" className="py-28 md:py-36 px-6 bg-black relative overflow-hidden" data-testid="features-section">
      {/* Background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,255,209,0.03)_0%,transparent_70%)]" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-20 md:mb-28"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FFD1]/10 border border-[#00FFD1]/20 mb-6"
            whileHover={{ scale: 1.02 }}
          >
            <Zap className="w-4 h-4 text-[#00FFD1]" />
            <span className="text-[#00FFD1] text-sm font-semibold tracking-wide">Powerful Features</span>
          </motion.div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight mb-6">
            Everything You Need.{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FFD1] to-[#00D4AA]">In One App.</span>
          </h2>
          <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            Training. Nutrition. Recovery. Performance.{" "}
            <span className="text-white">All connected in one intelligent system.</span>
          </p>
        </motion.div>

        {/* Feature Blocks */}
        <div className="space-y-24 md:space-y-32">
          {features.map((feature, index) => (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
              className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                index % 2 === 1 ? 'lg:grid-flow-dense' : ''
              }`}
              data-testid={`feature-block-${index}`}
            >
              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                <motion.div
                  initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                >
                  {/* Badge */}
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6">
                    <span className="text-[#00FFD1]">{feature.badgeIcon}</span>
                    <span className="text-gray-300 text-xs font-medium">{feature.badge}</span>
                  </div>

                  {/* Title */}
                  <h3 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6 leading-tight">
                    {feature.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-400 text-lg leading-relaxed mb-8 max-w-lg">
                    {feature.description}
                  </p>

                  {/* Learn More Link */}
                  <motion.button
                    onClick={() => scrollToSection(feature.scrollTo)}
                    className="inline-flex items-center gap-2 text-[#00FFD1] font-semibold group cursor-pointer"
                    whileHover={{ x: 5 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    Learn more
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </motion.button>
                </motion.div>
              </div>

              {/* Visual */}
              <motion.div
                className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.8 }}
                whileHover={{ scale: 1.02 }}
              >
                {getVisual(feature.visual)}
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="mt-28 md:mt-36 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight mb-6">
            Start Your Transformation Today
          </h3>
          <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
            Join thousands already evolving their fitness journey
          </p>
          
          {/* Email Form */}
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 bg-white/5 border border-white/10 text-white placeholder:text-gray-500 rounded-full h-14 px-6 focus:border-[#00FFD1] focus:ring-1 focus:ring-[#00FFD1] transition-all outline-none"
              data-testid="features-email-input"
              required
            />
            <motion.button
              type="submit"
              disabled={isLoading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="h-14 px-8 bg-gradient-to-r from-[#00FFD1] to-[#00D4AA] text-black font-bold rounded-full shadow-[0_0_30px_rgba(0,255,209,0.3)] hover:shadow-[0_0_40px_rgba(0,255,209,0.5)] transition-all disabled:opacity-70 flex items-center justify-center gap-2"
              data-testid="features-submit-btn"
            >
              {isLoading ? "Joining..." : "Join Early Access"}
              {!isLoading && <ChevronRight className="w-4 h-4" />}
            </motion.button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

// Keep EcosystemSection for backward compatibility but use FeaturesSection
const EcosystemSection = FeaturesSection;

// ============================================
// 3. SMART NUTRITION FEATURES SECTION
// ============================================
const NutritionSection = () => {
  const features = [
    { icon: <Camera className="w-5 h-5" />, title: "AI Food Recognition", desc: "Snap a photo, get instant nutrition data" },
    { icon: <BarChart3 className="w-5 h-5" />, title: "AI Calorie Tracking", desc: "Automatic, intelligent tracking" },
    { icon: <PieChart className="w-5 h-5" />, title: "Macro Tracking", desc: "Protein, carbs, fats breakdown" },
    { icon: <Calendar className="w-5 h-5" />, title: "Meal Logging", desc: "Quick and easy food diary" },
    { icon: <TrendingUp className="w-5 h-5" />, title: "Progress Tracking", desc: "See your journey unfold" },
    { icon: <Target className="w-5 h-5" />, title: "Daily Targets", desc: "Personalized calorie goals" },
  ];

  return (
    <section id="nutrition" className="py-24 md:py-32 px-6" data-testid="nutrition-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FFD1]/10 border border-[#00FFD1]/20 mb-6">
              <Sparkles className="w-4 h-4 text-[#00FFD1]" />
              <span className="text-[#00FFD1] text-sm font-medium">AI-Powered Tools</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
              Smarter Nutrition{" "}
              <span className="text-[#00FFD1]">Starts Here</span>
            </h2>
            <p className="text-gray-400 text-lg mb-10">
              Track your nutrition effortlessly with AI-powered tools that learn your habits and help you reach your goals faster.
            </p>

            {/* Features Grid */}
            <div className="grid sm:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-[#00FFD1]/20 transition-colors"
                  data-testid={`nutrition-feature-${index}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#00FFD1]/10 flex items-center justify-center text-[#00FFD1] flex-shrink-0">
                    {feature.icon}
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm">{feature.title}</h4>
                    <p className="text-gray-500 text-xs mt-0.5">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right: Phone Mockup with App Dashboard */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative flex justify-center"
          >
            {/* Phone Frame */}
            <div className="relative">
              {/* Phone Outer Shell */}
              <div className="relative w-[280px] h-[580px] bg-gradient-to-b from-gray-800 to-gray-900 rounded-[3rem] p-2 shadow-2xl shadow-black/50">
                {/* Phone Inner Bezel */}
                <div className="relative w-full h-full bg-black rounded-[2.5rem] overflow-hidden">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-black rounded-b-2xl z-20" />
                  
                  {/* Screen Content */}
                  <div className="relative w-full h-full bg-[#0A0A0A] pt-10 px-4 pb-4 overflow-hidden">
                    {/* Status Bar */}
                    <div className="flex justify-between items-center text-white text-xs mb-4">
                      <span>9:41</span>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-2 border border-white rounded-sm">
                          <div className="w-3/4 h-full bg-white rounded-sm" />
                        </div>
                      </div>
                    </div>

                    {/* App Content */}
                    <div className="space-y-3">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <p className="text-gray-500 text-[10px]">Today's Progress</p>
                          <p className="text-white font-bold text-lg">1,847 <span className="text-gray-500 text-[10px] font-normal">/ 2,200 cal</span></p>
                        </div>
                        <div className="w-12 h-12 rounded-full border-[3px] border-[#00FFD1] flex items-center justify-center">
                          <span className="text-[#00FFD1] font-bold text-xs">84%</span>
                        </div>
                      </div>

                      {/* Macros */}
                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-white/5 rounded-lg p-2 text-center">
                          <p className="text-[#00FFD1] font-bold text-sm">145g</p>
                          <p className="text-gray-500 text-[8px]">Protein</p>
                          <div className="mt-1 h-1 bg-white/10 rounded-full overflow-hidden">
                            <div className="h-full bg-[#00FFD1] w-[72%]" />
                          </div>
                        </div>
                        <div className="bg-white/5 rounded-lg p-2 text-center">
                          <p className="text-blue-400 font-bold text-sm">210g</p>
                          <p className="text-gray-500 text-[8px]">Carbs</p>
                          <div className="mt-1 h-1 bg-white/10 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-400 w-[85%]" />
                          </div>
                        </div>
                        <div className="bg-white/5 rounded-lg p-2 text-center">
                          <p className="text-orange-400 font-bold text-sm">62g</p>
                          <p className="text-gray-500 text-[8px]">Fat</p>
                          <div className="mt-1 h-1 bg-white/10 rounded-full overflow-hidden">
                            <div className="h-full bg-orange-400 w-[65%]" />
                          </div>
                        </div>
                      </div>

                      {/* Meal Cards */}
                      <div className="space-y-2 mt-3">
                        <p className="text-gray-500 text-[8px] uppercase tracking-wider">Today's Meals</p>
                        {[
                          { meal: "Breakfast", cal: 420, items: "Oatmeal, Banana, Shake" },
                          { meal: "Lunch", cal: 650, items: "Chicken, Rice, Veggies" },
                          { meal: "Dinner", cal: 580, items: "Salmon, Sweet Potato" },
                        ].map((meal, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-white/[0.02] rounded-lg border border-white/5">
                            <div>
                              <p className="text-white text-[10px] font-medium">{meal.meal}</p>
                              <p className="text-gray-500 text-[8px]">{meal.items}</p>
                            </div>
                            <span className="text-[#00FFD1] font-semibold text-[10px]">{meal.cal}</span>
                          </div>
                        ))}
                      </div>

                      {/* Add Meal Button */}
                      <button className="w-full py-2 bg-[#00FFD1] text-black text-xs font-bold rounded-lg mt-2">
                        + Log Meal
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative glow behind phone */}
              <div className="absolute -inset-10 bg-[#00FFD1]/10 rounded-full blur-3xl -z-10" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// 4. LOCAL TRAINER CONNECTION SECTION
// ============================================
const TrainerSection = () => {
  const highlights = [
    { icon: <MapPin className="w-5 h-5" />, text: "Location-based trainer search" },
    { icon: <UserCheck className="w-5 h-5" />, text: "Browse trainer profiles" },
    { icon: <Star className="w-5 h-5" />, text: "View specialties and services" },
    { icon: <Search className="w-5 h-5" />, text: "Compare options easily" },
    { icon: <Target className="w-5 h-5" />, text: "Find the right fit for your goals" },
  ];

  return (
    <section id="trainers" className="py-24 md:py-32 px-6 bg-[#0A0A0A]" data-testid="trainer-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Phone Mockup with Trainer Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative order-2 lg:order-1 flex justify-center"
          >
            {/* Realistic iPhone Frame */}
            <div className="relative">
              {/* iPhone Outer Shell */}
              <div className="relative w-[280px] bg-gradient-to-b from-[#2a2a2e] to-[#1a1a1e] rounded-[3rem] p-3 shadow-2xl shadow-black/50 border border-white/10">
                {/* Dynamic Island */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-7 bg-black rounded-full z-30" />
                
                {/* Phone Inner */}
                <div className="relative w-full bg-black rounded-[2.5rem] overflow-hidden">
                  {/* Screen Content */}
                  <div className="relative aspect-[9/19.5] overflow-hidden">
                    {/* Background Image */}
                    <img
                      src="https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?q=85&w=400&auto=format&fit=crop"
                      alt="Personal trainer"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                    
                    {/* Status Bar */}
                    <div className="relative flex justify-between items-center text-white text-xs px-6 pt-14">
                      <span>9:41</span>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-2 border border-white rounded-sm">
                          <div className="w-3/4 h-full bg-white rounded-sm" />
                        </div>
                      </div>
                    </div>

                    {/* Content Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      {/* Trainer Info Card */}
                      <div className="bg-black/70 backdrop-blur-xl rounded-2xl p-4 border border-white/20">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-white font-bold text-base">Marcus Johnson</h3>
                            <p className="text-[#00FFD1] text-xs">Certified Personal Trainer</p>
                          </div>
                          <div className="flex items-center gap-1 bg-[#00FFD1]/20 px-2 py-1 rounded-full border border-[#00FFD1]/30">
                            <Star className="w-3 h-3 text-[#00FFD1] fill-[#00FFD1]" />
                            <span className="text-[#00FFD1] text-xs font-bold">4.9</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1 text-gray-400 text-[10px] mb-3">
                          <MapPin className="w-3 h-3" />
                          <span>2.3 miles away • Los Angeles, CA</span>
                        </div>

                        <div className="flex gap-2 mb-3">
                          <span className="px-2 py-1 bg-white/10 rounded-full text-[8px] text-gray-300">Strength</span>
                          <span className="px-2 py-1 bg-white/10 rounded-full text-[8px] text-gray-300">HIIT</span>
                          <span className="px-2 py-1 bg-white/10 rounded-full text-[8px] text-gray-300">Weight Loss</span>
                        </div>

                        <button className="w-full py-2.5 bg-[#00FFD1] text-black text-xs font-bold rounded-xl">
                          View Profile
                        </button>
                      </div>

                      {/* Navigation Dots */}
                      <div className="flex justify-center gap-1 mt-3">
                        <div className="w-2 h-2 rounded-full bg-[#00FFD1]" />
                        <div className="w-2 h-2 rounded-full bg-white/30" />
                        <div className="w-2 h-2 rounded-full bg-white/30" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative glow behind phone */}
              <div className="absolute -inset-10 bg-[#00FFD1]/10 rounded-full blur-3xl -z-10" />
            </div>
          </motion.div>

          {/* Right: Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="order-1 lg:order-2"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FFD1]/10 border border-[#00FFD1]/20 mb-6">
              <Users className="w-4 h-4 text-[#00FFD1]" />
              <span className="text-[#00FFD1] text-sm font-medium">Local Discovery</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
              Connect With Trainers{" "}
              <span className="text-[#00FFD1]">Near You</span>
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              Discover, browse, and connect with trainers and fitness professionals in your local area. Find the perfect match for your fitness journey.
            </p>

            {/* Highlights */}
            <div className="space-y-4">
              {highlights.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-[#00FFD1]/20 transition-colors"
                  data-testid={`trainer-highlight-${index}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#00FFD1]/10 flex items-center justify-center text-[#00FFD1]">
                    {item.icon}
                  </div>
                  <span className="text-white font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// 5. LOCAL SERVICES SECTION
// ============================================
const LocalServicesSection = () => {
  const services = [
    {
      icon: <Utensils className="w-8 h-8" />,
      title: "Meal Prep Companies",
      description: "Fresh, healthy meals delivered to your door from local providers.",
      image: "https://images.unsplash.com/photo-1543352632-5a4b24e4d2a6?q=85&w=600&auto=format&fit=crop"
    },
    {
      icon: <Apple className="w-8 h-8" />,
      title: "Nutrition Services",
      description: "Expert guidance from certified nutritionists in your area.",
      image: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?q=85&w=600&auto=format&fit=crop"
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Recovery Services",
      description: "Massage, physical therapy, and wellness treatments nearby.",
      image: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?q=85&w=600&auto=format&fit=crop"
    },
  ];

  return (
    <section className="py-24 md:py-32 px-6" data-testid="local-services-section">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FFD1]/10 border border-[#00FFD1]/20 mb-6">
            <MapPin className="w-4 h-4 text-[#00FFD1]" />
            <span className="text-[#00FFD1] text-sm font-medium">Local Ecosystem</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-4">
            Find Local Support{" "}
            <span className="text-[#00FFD1]">Beyond the Gym</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            EVOLVE connects you to a complete wellness ecosystem in your area
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="group relative rounded-3xl overflow-hidden bg-[#0A0A0A] border border-white/5 hover:border-[#00FFD1]/30 transition-all duration-500"
              data-testid={`local-service-${index}`}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="w-14 h-14 rounded-2xl bg-[#00FFD1]/10 flex items-center justify-center text-[#00FFD1] mb-4 -mt-12 relative z-10 border border-[#00FFD1]/20">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{service.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{service.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// 5.5 SUCCESS STORIES / TESTIMONIALS
// ============================================
const SuccessStoriesSection = () => {
  const testimonials = [
    {
      name: "Marcus T.",
      role: "Lost 45 lbs in 6 months",
      image: "https://images.unsplash.com/photo-1682241367368-6387d5d4921a?w=400&h=400&fit=crop&crop=face",
      quote: "EVOLVE completely changed my approach to fitness. The AI tracking made calorie counting effortless, and finding a local trainer who understood my goals was a game-changer.",
      stats: { weight: "-45 lbs", time: "6 months", workouts: "120+" }
    },
    {
      name: "Sarah K.",
      role: "Marathon Runner",
      image: "https://images.unsplash.com/photo-1597079997518-cd1fa15d42e0?w=400&h=400&fit=crop&crop=face",
      quote: "As a busy professional, I needed something that fit my schedule. EVOLVE's meal prep connections and smart tracking helped me fuel my training properly.",
      stats: { weight: "-22 lbs", time: "4 months", workouts: "85+" }
    },
    {
      name: "David R.",
      role: "Gained 25 lbs muscle",
      image: "https://images.unsplash.com/photo-1744551154623-4b5336e95c28?w=400&h=400&fit=crop&crop=face",
      quote: "The nutrition tracking is incredibly accurate. Scanning my meals takes seconds, and the macro breakdown helped me hit my protein goals consistently.",
      stats: { weight: "+25 lbs", time: "8 months", workouts: "200+" }
    }
  ];

  return (
    <section className="py-24 md:py-32 px-6 bg-black" data-testid="success-stories-section">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FFD1]/10 border border-[#00FFD1]/20 mb-6">
            <Star className="w-4 h-4 text-[#00FFD1]" />
            <span className="text-[#00FFD1] text-sm font-medium">Success Stories</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-4">
            Real People.{" "}
            <span className="text-[#00FFD1]">Real Results.</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Join thousands who have transformed their lives with EVOLVE
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.6 }}
              className="relative group"
            >
              {/* Card */}
              <div className="bg-gradient-to-b from-[#0A0A0A] to-[#050505] rounded-3xl p-6 border border-white/5 hover:border-[#00FFD1]/30 transition-all duration-500 h-full flex flex-col">
                {/* Quote Icon */}
                <div className="text-[#00FFD1]/30 text-6xl font-serif leading-none mb-4">"</div>
                
                {/* Quote */}
                <p className="text-gray-300 text-sm leading-relaxed mb-6 flex-grow">
                  {testimonial.quote}
                </p>

                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-3 mb-6 py-4 border-y border-white/5">
                  <div className="text-center">
                    <p className="text-[#00FFD1] text-lg font-black">{testimonial.stats.weight}</p>
                    <p className="text-gray-500 text-[10px] uppercase tracking-wider">Weight</p>
                  </div>
                  <div className="text-center">
                    <p className="text-white text-lg font-black">{testimonial.stats.time}</p>
                    <p className="text-gray-500 text-[10px] uppercase tracking-wider">Duration</p>
                  </div>
                  <div className="text-center">
                    <p className="text-white text-lg font-black">{testimonial.stats.workouts}</p>
                    <p className="text-gray-500 text-[10px] uppercase tracking-wider">Workouts</p>
                  </div>
                </div>

                {/* Author */}
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.name}
                      className="w-14 h-14 rounded-full object-cover border-2 border-[#00FFD1]/30"
                    />
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#00FFD1] rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-black" />
                    </div>
                  </div>
                  <div>
                    <p className="text-white font-semibold">{testimonial.name}</p>
                    <p className="text-[#00FFD1] text-xs">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Stats Banner */}
        <motion.div
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          {[
            { value: "50K+", label: "Active Users" },
            { value: "2M+", label: "Meals Tracked" },
            { value: "98%", label: "Success Rate" },
            { value: "4.9", label: "App Rating" }
          ].map((stat, index) => (
            <div key={index} className="text-center p-6 bg-white/[0.02] rounded-2xl border border-white/5">
              <p className="text-3xl md:text-4xl font-black text-[#00FFD1]">{stat.value}</p>
              <p className="text-gray-400 text-sm mt-1">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// 6. HOW EVOLVE WORKS
// ============================================
const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      title: "Enter Your Location & Goals",
      description: "Tell us where you are and what you want to achieve.",
      icon: <MapPin className="w-6 h-6" />
    },
    {
      number: "02",
      title: "Discover Local Services",
      description: "Find trainers, meal prep, and wellness services near you.",
      icon: <Search className="w-6 h-6" />
    },
    {
      number: "03",
      title: "Track With AI Tools",
      description: "Log calories, food, and progress with intelligent tracking.",
      icon: <Sparkles className="w-6 h-6" />
    },
    {
      number: "04",
      title: "Build Your Lifestyle",
      description: "Create your personalized fitness ecosystem with EVOLVE.",
      icon: <Zap className="w-6 h-6" />
    },
  ];

  return (
    <section className="py-24 md:py-32 px-6 bg-[#0A0A0A]" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-4">
            How <span className="text-[#00FFD1]">EVOLVE</span> Works
          </h2>
          <p className="text-gray-400 text-lg">Four simple steps to transform your fitness journey</p>
        </motion.div>

        {/* Steps */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="relative p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-[#00FFD1]/20 transition-colors group"
              data-testid={`step-${index}`}
            >
              {/* Step Number */}
              <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-[#00FFD1] flex items-center justify-center text-black font-bold text-sm">
                {step.number}
              </div>

              {/* Icon */}
              <div className="w-14 h-14 rounded-2xl bg-[#00FFD1]/10 flex items-center justify-center text-[#00FFD1] mb-4 mt-4 group-hover:scale-110 transition-transform">
                {step.icon}
              </div>

              {/* Content */}
              <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>

              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-px bg-gradient-to-r from-[#00FFD1]/50 to-transparent" />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// 7. WHO EVOLVE IS FOR
// ============================================
const AudienceSection = () => {
  const audiences = [
    { icon: <Zap className="w-6 h-6" />, title: "Fitness Beginners", description: "Start your journey with guidance and local support" },
    { icon: <Clock className="w-6 h-6" />, title: "Busy Professionals", description: "Efficient tracking and nearby services that fit your schedule" },
    { icon: <Target className="w-6 h-6" />, title: "Athletes", description: "Advanced tracking and performance optimization" },
    { icon: <Users className="w-6 h-6" />, title: "Accountability Seekers", description: "Connect with local trainers and stay on track" },
    { icon: <BarChart3 className="w-6 h-6" />, title: "Data-Driven Users", description: "Smart nutrition tracking with AI insights" },
    { icon: <MapPin className="w-6 h-6" />, title: "Local Supporters", description: "Find trusted services in your community" },
  ];

  return (
    <section className="py-24 md:py-32 px-6" data-testid="audience-section">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-4">
            Who Is <span className="text-[#00FFD1]">EVOLVE</span> For?
          </h2>
          <p className="text-gray-400 text-lg">Built for everyone on their fitness journey</p>
        </motion.div>

        {/* Audience Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {audiences.map((audience, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-[#00FFD1]/30 transition-all duration-500 group"
              data-testid={`audience-card-${index}`}
            >
              <div className="w-14 h-14 rounded-2xl bg-[#00FFD1]/10 flex items-center justify-center text-[#00FFD1] mb-4 group-hover:scale-110 transition-transform">
                {audience.icon}
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{audience.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{audience.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// TECHNOLOGY SECTION
// ============================================
const TechnologySection = () => {
  const techFeatures = [
    {
      icon: <Cpu className="w-8 h-8" />,
      title: "AI Engine",
      description: "Our proprietary AI learns your habits, preferences, and goals to deliver personalized recommendations that evolve with you.",
      stats: "99.2% Accuracy"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Smart Data Tracking",
      description: "Advanced algorithms process your nutrition, workout, and biometric data in real-time for actionable insights.",
      stats: "Real-time Analytics"
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "Seamless Integrations",
      description: "Connect with Apple Health, Google Fit, Fitbit, and 50+ other health apps for a unified fitness ecosystem.",
      stats: "50+ Integrations"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Privacy First",
      description: "Your health data is encrypted and protected. You control who sees your information, always.",
      stats: "Bank-level Security"
    }
  ];

  return (
    <section id="technology" className="py-24 md:py-32 px-6" data-testid="technology-section">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FFD1]/10 border border-[#00FFD1]/20 mb-6">
            <Cpu className="w-4 h-4 text-[#00FFD1]" />
            <span className="text-[#00FFD1] text-sm font-medium">Advanced Technology</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-4">
            Powered by <span className="text-[#00FFD1]">Innovation</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Built on cutting-edge AI and machine learning to deliver the most personalized fitness experience
          </p>
        </motion.div>

        {/* Tech Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {techFeatures.map((tech, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="p-8 rounded-3xl bg-gradient-to-br from-white/[0.03] to-transparent border border-white/5 hover:border-[#00FFD1]/30 transition-all duration-500 group"
              data-testid={`tech-card-${index}`}
            >
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 rounded-2xl bg-[#00FFD1]/10 flex items-center justify-center text-[#00FFD1] flex-shrink-0 group-hover:scale-110 transition-transform">
                  {tech.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-bold text-white">{tech.title}</h3>
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-[#00FFD1]/10 text-[#00FFD1]">
                      {tech.stats}
                    </span>
                  </div>
                  <p className="text-gray-400 leading-relaxed">{tech.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// PRICING SECTION - PREMIUM REBUILD
// ============================================
const PricingSection = () => {
  const [isAnnual, setIsAnnual] = useState(true);

  const plans = [
    {
      name: "Free",
      monthlyPrice: 0,
      annualPrice: 0,
      period: "forever",
      description: "Perfect for getting started",
      features: [
        "Basic calorie tracking",
        "Manual food logging",
        "Access to trainer directory",
        "Community support",
        "Progress tracking",
      ],
      cta: "Get Started",
      popular: false
    },
    {
      name: "Premium",
      monthlyPrice: 19,
      annualPrice: 12,
      annualTotal: 144,
      savingsAmount: 84,
      period: isAnnual ? "month, billed annually" : "month",
      description: "Unlock your full potential",
      features: [
        "AI-powered food recognition",
        "Unlimited macro tracking",
        "Personalized meal plans",
        "Direct trainer messaging",
        "Advanced analytics dashboard",
        "Priority support",
        "Exclusive partner discounts",
        "Custom workout plans"
      ],
      cta: "Start Free Trial",
      popular: true
    }
  ];

  const displayPrice = (plan) => {
    return isAnnual ? plan.annualPrice : plan.monthlyPrice;
  };

  return (
    <section id="pricing" className="py-28 md:py-36 px-6 bg-black relative overflow-hidden" data-testid="pricing-section">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,255,209,0.05)_0%,transparent_50%)]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#00FFD1]/5 rounded-full blur-[120px] pointer-events-none" />
      
      <div className="max-w-5xl mx-auto relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00FFD1]/10 border border-[#00FFD1]/20 mb-6"
            whileHover={{ scale: 1.02 }}
          >
            <Sparkles className="w-4 h-4 text-[#00FFD1]" />
            <span className="text-[#00FFD1] text-sm font-semibold tracking-wide">Simple, Transparent Pricing</span>
          </motion.div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight mb-5">
            Invest in <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00FFD1] to-[#00D4AA]">Yourself</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            Join thousands transforming their fitness journey
          </p>
        </motion.div>

        {/* Pill Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex justify-center mb-14"
        >
          <div className="relative inline-flex p-1.5 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full">
            {/* Sliding Background Pill */}
            <motion.div
              className="absolute top-1.5 bottom-1.5 rounded-full bg-gradient-to-r from-[#00FFD1] to-[#00D4AA] shadow-[0_0_20px_rgba(0,255,209,0.4)]"
              initial={false}
              animate={{
                left: isAnnual ? '50%' : '6px',
                right: isAnnual ? '6px' : '50%',
              }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            />
            
            {/* Monthly Button */}
            <button
              onClick={() => setIsAnnual(false)}
              className={`relative z-10 px-6 sm:px-8 py-3 text-sm font-semibold rounded-full transition-colors duration-300 ${
                !isAnnual ? 'text-black' : 'text-white/60 hover:text-white/80'
              }`}
              data-testid="monthly-toggle"
            >
              Monthly
            </button>
            
            {/* Annual Button */}
            <button
              onClick={() => setIsAnnual(true)}
              className={`relative z-10 px-6 sm:px-8 py-3 text-sm font-semibold rounded-full transition-colors duration-300 flex items-center gap-2 ${
                isAnnual ? 'text-black' : 'text-white/60 hover:text-white/80'
              }`}
              data-testid="annual-toggle"
            >
              Annual
              <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full transition-all duration-300 ${
                isAnnual 
                  ? 'bg-black/20 text-black' 
                  : 'bg-[#00FFD1]/20 text-[#00FFD1]'
              }`}>
                SAVE 37%
              </span>
            </button>
          </div>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.6 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className={`relative group ${plan.popular ? 'md:-mt-4 md:mb-4' : ''}`}
              data-testid={`pricing-card-${index}`}
            >
              {/* Card */}
              <div className={`relative h-full p-8 lg:p-10 rounded-3xl backdrop-blur-xl transition-all duration-500 ${
                plan.popular
                  ? 'bg-gradient-to-b from-white/[0.08] to-white/[0.02] border-2 border-[#00FFD1]/40 shadow-[0_0_60px_-15px_rgba(0,255,209,0.3)]'
                  : 'bg-white/[0.03] border border-white/10 hover:border-white/20'
              }`}>
                
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <motion.div 
                      className="px-5 py-1.5 bg-gradient-to-r from-[#00FFD1] to-[#00D4AA] text-black text-xs font-bold rounded-full shadow-[0_4px_20px_rgba(0,255,209,0.4)]"
                      initial={{ scale: 0.9 }}
                      animate={{ scale: 1 }}
                      transition={{ repeat: Infinity, repeatType: "reverse", duration: 2 }}
                    >
                      MOST POPULAR
                    </motion.div>
                  </div>
                )}

                {/* Plan Name */}
                <div className="mb-6">
                  <h3 className={`text-xl font-bold mb-1 ${plan.popular ? 'text-[#00FFD1]' : 'text-white'}`}>
                    {plan.name}
                  </h3>
                  <p className="text-gray-500 text-sm">{plan.description}</p>
                </div>

                {/* Price */}
                <div className="mb-8">
                  <div className="flex items-baseline gap-1">
                    <span className="text-white/40 text-2xl font-medium">$</span>
                    <motion.span
                      key={displayPrice(plan)}
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      className="text-6xl lg:text-7xl font-black text-white tracking-tight"
                    >
                      {displayPrice(plan)}
                    </motion.span>
                  </div>
                  <p className="text-gray-500 text-sm mt-1">
                    {plan.monthlyPrice === 0 ? 'Free forever' : `per ${plan.period}`}
                  </p>
                  
                  {/* Savings Badge */}
                  {plan.popular && isAnnual && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-full bg-[#00FFD1]/10 border border-[#00FFD1]/20"
                    >
                      <Zap className="w-3.5 h-3.5 text-[#00FFD1]" />
                      <span className="text-[#00FFD1] text-sm font-semibold">
                        Save ${plan.savingsAmount}/year
                      </span>
                    </motion.div>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, fIndex) => (
                    <motion.li 
                      key={fIndex} 
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + fIndex * 0.05 }}
                    >
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        plan.popular ? 'bg-[#00FFD1]/20' : 'bg-white/10'
                      }`}>
                        <Check className={`w-3 h-3 ${plan.popular ? 'text-[#00FFD1]' : 'text-white/70'}`} />
                      </div>
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </motion.li>
                  ))}
                </ul>

                {/* CTA Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full py-4 rounded-2xl font-bold text-base transition-all duration-300 ${
                    plan.popular
                      ? 'bg-gradient-to-r from-[#00FFD1] to-[#00D4AA] text-black shadow-[0_4px_30px_rgba(0,255,209,0.3)] hover:shadow-[0_4px_40px_rgba(0,255,209,0.5)]'
                      : 'bg-white/10 text-white border border-white/10 hover:bg-white/15 hover:border-white/20'
                  }`}
                >
                  {plan.cta}
                </motion.button>

                {/* Trust Text */}
                {plan.popular && (
                  <p className="text-center text-gray-500 text-xs mt-4">
                    7-day free trial • Cancel anytime
                  </p>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Enterprise CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-center mt-14"
        >
          <p className="text-gray-500 text-sm">
            Building for a team or gym?{" "}
            <a 
              href="mailto:enterprise@evolvewellnessapp.com" 
              className="text-[#00FFD1] hover:text-[#00FFD1]/80 font-medium transition-colors inline-flex items-center gap-1 group"
            >
              Contact for Enterprise
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// 8. FINAL CTA SECTION
// ============================================
const CTASection = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/waitlist`, { email });
      if (response.data.success) {
        toast.success(response.data.message);
        setEmail("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="py-24 md:py-32 px-6 bg-[#0A0A0A] relative overflow-hidden" data-testid="cta-section" id="partner">
      {/* Background glow */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,255,209,0.1)_0%,transparent_60%)]" />
      
      <div className="max-w-3xl mx-auto text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-6">
            Your Local Fitness & Wellness Network{" "}
            <span className="text-[#00FFD1]">Starts Here</span>
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-xl mx-auto">
            Join EVOLVE for smarter tracking, stronger connections, and better results.
          </p>

          {/* Email Form */}
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto mb-6">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 bg-white/10 border border-white/20 text-white placeholder:text-gray-400 rounded-full h-14 px-6 focus:border-[#00FFD1] focus:ring-1 focus:ring-[#00FFD1] transition-all outline-none"
              data-testid="cta-email-input"
              required
            />
            <button
              type="submit"
              disabled={isLoading}
              className="h-14 px-8 bg-[#00FFD1] text-black font-bold rounded-full hover:bg-[#00FFD1]/90 transition-all shadow-[0_0_20px_rgba(0,255,209,0.3)] hover:shadow-[0_0_30px_rgba(0,255,209,0.5)] disabled:opacity-70 flex items-center justify-center gap-2"
              data-testid="cta-submit-btn"
            >
              {isLoading ? "Joining..." : "Join Early Access"}
              {!isLoading && <ChevronRight className="w-4 h-4" />}
            </button>
          </form>

          {/* Secondary CTA */}
          <a
            href="/partner-apply"
            className="inline-flex items-center gap-2 text-white/80 hover:text-[#00FFD1] transition-colors group"
            data-testid="cta-partner-link"
          >
            <span className="font-medium">Apply as a Trainer or Partner</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================
// 9. FOOTER
// ============================================
const Footer = () => {
  return (
    <footer className="py-12 px-6 border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Logo */}
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_evolve-fitness-5/artifacts/uqt72vx4_IMG_1561.png" 
              alt="EVOLVE Logo" 
              className="h-40 md:h-48 w-auto object-contain"
            />
          </div>

          {/* Links */}
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <a href="/privacy" className="hover:text-white transition-colors" data-testid="privacy-link">Privacy Policy</a>
            <a href="/terms" className="hover:text-white transition-colors" data-testid="terms-link">Terms of Service</a>
            <a href="mailto:contact@evolvewellnessapp.com" className="hover:text-white transition-colors" data-testid="contact-link">Contact</a>
          </div>

          {/* Copyright */}
          <p className="text-gray-600 text-sm">© 2025 EVOLVE Wellness</p>
        </div>
      </div>
    </footer>
  );
};

// ============================================
// MAIN LANDING PAGE
// ============================================
const LandingPage = () => {
  return (
    <main className="min-h-screen bg-black" data-testid="landing-page">
      <Navigation />
      <HeroSection />
      <EcosystemSection />
      <NutritionSection />
      <TechnologySection />
      <TrainerSection />
      <LocalServicesSection />
      <SuccessStoriesSection />
      <HowItWorksSection />
      <AudienceSection />
      <PricingSection />
      <CTASection />
      <Footer />
    </main>
  );
};

export default LandingPage;
